import get from 'lodash.get';
import mapValues from 'lodash.mapvalues';
import zipObject from 'lodash.zipobject';
import isEmpty from 'lodash.isempty';
import { getD8RecommendedMeasures } from './getD8RecommendedMeasures';
import { getTRCRecommendation } from './getTRCRecommendation';
import { getD8NextForm, getLecturaNextForm, getBurstNextForm } from './getNextForm';
import { BURST, DIBELS8, IDEL2, R3D, R3DSPANISH } from '../../constants/applications';
import { GRADE } from '../../constants/enrollment';
import { getBurstRecommendation } from './getBurstRecommendation';
import { MEASURE_NAMES, PM_MEASURES_BY_APPLICATION } from '../../constants/assessments';
import { getLecturaRecommendedMeasures } from './getLecturaRecommendedMeasures';
import { getRecommendationStatusesPerApplication } from './getRecomendationStatuses';

/**
 * Get next form uids for application measures and particular grade
 * based on available forms and previous results.
 * @param {String} grade
 * @param {Object} studentResults results map by app and period
 * @param {Object} formsMap all forms map
 * @param {Object} measures application to measures map
 * @returns {Object} map containing recommended form uid in the format:
 *  {
 *      <application>: {
 *          <measure>: <formUid>
 *      }
 *  }
 */
export const getNextFormUidsByApplicationMeasure = (grade, studentResults, formsMap, measures) =>
    Object.keys(measures).reduce((nextFormUidByApp, application) => {
        let nextFormUid = {};
        if (application in studentResults) {
            if (application === DIBELS8) {
                nextFormUid = getD8NextForm(
                    grade,
                    studentResults[application],
                    formsMap,
                    measures[application],
                );
            } else if (application === IDEL2) {
                nextFormUid = getLecturaNextForm(
                    grade,
                    studentResults[application],
                    formsMap,
                    measures[application],
                );
            } else if (application === BURST) {
                const requiredMeasures = measures[application];
                const dibelsMeasures = requiredMeasures.filter(measure =>
                    PM_MEASURES_BY_APPLICATION[DIBELS8].includes(measure),
                );
                const burstMeasures = requiredMeasures.filter(measure =>
                    PM_MEASURES_BY_APPLICATION[BURST].includes(measure),
                );
                nextFormUid = {
                    ...getBurstNextForm(
                        grade,
                        studentResults[application],
                        formsMap,
                        burstMeasures,
                    ),
                    ...getD8NextForm(grade, studentResults[DIBELS8], formsMap, dibelsMeasures),
                };
            }
        }
        return isEmpty(nextFormUid)
            ? nextFormUidByApp
            : {
                  ...nextFormUidByApp,
                  [application]: nextFormUid,
              };
    }, {});

/**
 * Get next form uids for supported grades and requested measures
 * based on available forms and existing student results.
 * @param {Object} studentResults results map by app and period
 * @param {Object} formsMap all forms map
 * @param {Object} measures application to measures map
 * @returns {Object} map containing recommended form uid in the format:
 *  {
 *      <application>: {
 *          <measure>: {
 *              <grade>: <formUid>
 *          }
 *      }
 *  }
 */
export const getNextFormUidForAllGrades = (studentResults, formsMap, measures) => {
    const emptyFormUidsObject = mapValues(measures, measuresInApplication =>
        zipObject(measuresInApplication, new Array(measuresInApplication.length).fill({})),
    );
    return Object.values(GRADE).reduce((formUids, grade) => {
        const nextFormUidsByApplicationMeasure = getNextFormUidsByApplicationMeasure(
            grade,
            studentResults,
            formsMap,
            measures,
        );
        return mapValues(formUids, (measuresInApplication, application) =>
            mapValues(measuresInApplication, (grades, measure) => ({
                ...grades,
                [grade]: nextFormUidsByApplicationMeasure?.[application]?.[measure],
            })),
        );
    }, emptyFormUidsObject);
};

// Collect all results that are relevant for PM recommendation rules
export const getEligibleResults = (studentResults, formsMap, studentGrade, filteredMeasure = []) =>
    Object.keys(studentResults).reduce(
        (resultsByPeriod, period) => ({
            ...resultsByPeriod,
            [period]: studentResults[period].filter(result => {
                // filtering of any ignored measure and invalid results
                if (
                    (filteredMeasure.length > 0 && filteredMeasure.includes(result.measure)) ||
                    !result.isValid
                ) {
                    return false;
                }

                // filter out offgrade PM results and PM results with unknown form
                if (period === 'PM' && !formsMap[result.formUid ?? result.formId]) {
                    return false;
                }

                return period === 'PM'
                    ? formsMap[result?.formUid ?? result.formId].grade === studentGrade
                    : true;
            }),
        }),
        {},
    );

const getRecommendedMeasures = (
    student,
    studentResults,
    applications,
    currentBMPeriod,
    studentInstruction,
    formsMap,
) =>
    applications.reduce((recommendedMeasuresByApp, application) => {
        let appRecommendedMeasures = [];
        const hasInstruction = studentInstruction !== null;
        if (application !== BURST && hasInstruction) {
            return {
                ...recommendedMeasuresByApp,
                [application]: [],
            };
        }
        if (application in studentResults) {
            if (application === DIBELS8) {
                appRecommendedMeasures = getD8RecommendedMeasures(
                    student,
                    getEligibleResults(studentResults[application], formsMap, student.grade, [
                        MEASURE_NAMES.MAZE,
                    ]),
                );
            } else if (application === R3D || application === R3DSPANISH) {
                appRecommendedMeasures = getTRCRecommendation(
                    get(student, `appStatuses.${application}.${currentBMPeriod}`, {}),
                    studentResults[application],
                );
            } else if (application === BURST && hasInstruction) {
                appRecommendedMeasures = getBurstRecommendation(
                    student,
                    currentBMPeriod,
                    studentInstruction,
                    studentResults,
                );
            } else if (application === IDEL2) {
                appRecommendedMeasures = getLecturaRecommendedMeasures(
                    student,
                    getEligibleResults(studentResults[application], formsMap, student.grade),
                );
            }
        }
        return {
            ...recommendedMeasuresByApp,
            [application]: appRecommendedMeasures,
        };
    }, {});

/**
 * Get recommendation statuses (on/off track) based on PM recommended measures grouped by apps
 *
 * @param {Object} student student data with D8 app statuses including
 *                         interp and gating status
 * @param {Object} studentResults contains all student results grouped by period
 * @param {Object} applications with application data that contains all benchmark periods
 * @param {Array<String>} recommendedMeasures ['PSF', 'NWF] or [] if nothing to recommend
 * @param {String} currentBMPeriod benchmark period which current date falls into
 * @param {Object} nextGoalPeriod object with information about next goal period (MOY or EOY)
 * @param {Object} studentsGoals object with teacher set goals for all students
 * @param {Array<Object>} cutscores array with cutscore objects for all grades/periods/measures
 * @param {Object} studentsZoG zones of growth for all students
 * @param {object} formsMap all forms map
 * @returns {Object} map, recommendation statuses grouped by measure grouped by application:
 *                  {<application>: {<measure> : <recommendation status>}}
 */
export const getRecommendationStatuses = (
    student,
    studentResults,
    applications,
    recommendedMeasures,
    currentBMPeriod,
    nextGoalPeriod,
    cutscores,
    studentsGoals,
    studentsZoG,
    formsMap,
) =>
    applications.reduce((recommendationStatusesByApp, application) => {
        let appRecommendationStatuses = {};

        if (application in studentResults) {
            if (application === DIBELS8) {
                appRecommendationStatuses = getRecommendationStatusesPerApplication(
                    DIBELS8,
                    student,
                    getEligibleResults(studentResults[DIBELS8], formsMap, student.grade),
                    recommendedMeasures[DIBELS8],
                    currentBMPeriod,
                    nextGoalPeriod,
                    cutscores,
                    studentsGoals,
                    studentsZoG,
                );
            }
            if (application === IDEL2) {
                appRecommendationStatuses = getRecommendationStatusesPerApplication(
                    IDEL2,
                    student,
                    getEligibleResults(studentResults[IDEL2], formsMap, student.grade),
                    recommendedMeasures[IDEL2],
                    currentBMPeriod,
                    nextGoalPeriod,
                    cutscores,
                    studentsGoals,
                    studentsZoG,
                );
            }
        }

        return {
            ...recommendationStatusesByApp,
            [application]: appRecommendationStatuses,
        };
    }, {});

export default getRecommendedMeasures;
